import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link as RouterLink, useLocation  } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import {
  Box, Typography, Paper, TextField, FormControl,
  Select,
  MenuItem, ListSubheader, Divider, CircularProgress, Alert,
  useTheme, Tooltip, Button,
  Chip, Stack, alpha,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fetchRequest as fetchBlueprints } from '../../redux/actions/blueprints/list';
import { fetchRequest as fetchPublicBlueprints } from '../../redux/actions/blueprints/publicList';
import { fetchRequest as fetchSegments } from '../../redux/actions/segments/list';
import { getBlueprintDetail, clearState as clearBlueprintDetail } from '../../redux/actions/blueprints/detail';
import TrainingScheduleField from './TrainingScheduleField';
import TrainingCostEstimator from './TrainingCostEstimator';
import PipelinePreview from '../common/PipelinePreview';
import NeuralNetGraph from '../blueprints/NeuralNetGraph';
import { suggestPipelineDefaults } from '../../utils/dataSufficiency';
import { fetchRequest as fetchBalance } from '../../redux/actions/billing/balance';
import { PipelinesIcon, ModelsIcon, SegmentsIcon } from '../global/CustomIcons';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';
import InfoIcon from '@mui/icons-material/Info';
import InsightsIcon from '@mui/icons-material/Insights';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { createModelRequest, clearState } from '../../redux/actions/models/create';
import PageBreadcrumbs from '../common/PageBreadcrumbs';

// Pacing presets for the new-data trigger: the minimum gap between
// data-triggered runs (``retrain_triggers.on_new_data.cooldown_seconds``
// on the backend). This is what keeps a continuous stream — rows landing
// every second — from retraining back-to-back all day.
const RETRAIN_PACING_OPTIONS = [
  { value: 900, label: 'Every 15 min' },
  { value: 1800, label: 'Every 30 min' },
  { value: 3600, label: 'Every hour' },
  { value: 10800, label: 'Every 3 hours' },
  { value: 21600, label: 'Every 6 hours' },
  { value: 86400, label: 'Once a day' },
];
const DEFAULT_RETRAIN_PACING = 1800;

// Help text for fields
const helpTexts = {
  blueprint: "Pick one or more models — your own custom models or pretrained foundation models, in any mix. They form a pool: every run trains all of them on the same segment data, and the best result becomes the promotion candidate. Each extra model adds one full training's cost per run.",
  segment: "The segment contains the data that will be used to train the model.",
  forecast_horizon: "Number of future points to forecast ahead of the current time.",
  training_schedule: "Defines when the model will automatically train.",
  training_mode: "What triggers a training run: exactly one run at creation, a recurring schedule, or the arrival of new data in the segment.",
  promotion_policy: "What happens after a run's best training beats the version that's serving: promote it automatically (retiring the old one), ask you first, or do nothing.",
  budget: "Optional monthly credit cap for this pipeline's trainings. When reached, scheduled and data-triggered runs pause until next month — manual runs still work.",
};

// Plain, compact text field matching the goal- and segment-studio look so
// the create surfaces stay visually uniform across the app.
const CMDarkTextField = ({ label, ...props }) => (
  <TextField variant="outlined" label={label} fullWidth size="small" {...props} />
);

const CreateModelPage = ({
  fetchBlueprints,
  fetchPublicFoundationModels,
  fetchSegments,
  createModel,
  modelCreating,
  modelCreateResponse,
  modelCreateError,
  blueprints,
  foundationModels,
  segments,
  blueprintsLoading,
  segmentsLoading,
  foundationDetail,
  foundationLoading,
  foundationError,
  loadBalance,
  balance,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();

  // ---- Foundation-mode detection -----------------------------------------
  // ``/pipelines/create?foundation=<backbone-uid>`` puts the page into
  // foundation-tuning mode: no blueprint document is created or
  // referenced, the user just picks an intensity (+ lookback if the
  // backbone needs it) and the trainer synthesizes the recipe at
  // dispatch time. The single ``foundation`` query param is enough -
  // we fetch the backbone metadata (which already lives in the
  // synthesized public-blueprint detail endpoint) and render
  // appropriately.
  const foundationUid = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get('foundation') || '';
  }, [location.search]);
  const isFoundationMode = !!foundationUid;
  const foundationMeta = (foundationDetail && foundationDetail.foundation) || null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [workspaceId, setWorkspaceId] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [clonedConfig, setClonedConfig] = useState(null);
  const [cloneWarnings, setCloneWarnings] = useState([]);
  const [componentKey, setComponentKey] = useState(0); // For forcing re-renders

  // Form values
  const [formValues, setFormValues] = useState({
    name: '',
    blueprint: '',
    segment: '',
    training_schedule: {
      start_time: '00:00:00',
      end_time: '23:59:59',
      // Default is "Run once": no recurring days means the scheduler
      // never fires — the backend queues one immediate run at create.
      days: [],
      frequency: '86400'
    },
    forecast_horizon: 1,
    // Automation settings (flattened for form handling; assembled
    // into the API shape in handleSubmit).
    promotion_mode: 'manual',            // 'auto' | 'manual' | 'off'
    training_mode: 'once',               // 'once' | 'schedule' | 'data'
    retrain_min_rows: 100,
    retrain_cooldown: DEFAULT_RETRAIN_PACING,  // min gap between data-triggered runs (s)
    monthly_budget: '',                  // '' = no cap
    challenger_blueprints: [],
    // Foundation-mode-only knobs. Ignored by the backend in
    // blueprint-mode; trimmed out of the submit payload so they don't
    // muddy the request body.
    training_intensity: 'adapt',
    lookback: ''
  });

  // Pre-select a model when navigated from a model detail page
  // ("Train on my data" / "Use in a pipeline" pass the blueprint uid
  // via router state).
  useEffect(() => {
    const preselected = location.state?.blueprintId;
    if (preselected) {
      setFormValues((prev) => (
        prev.blueprint ? prev : { ...prev, blueprint: preselected }
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Tracks which fields the user has explicitly typed into. We only
  // auto-default fields the user hasn't touched, so picking a new
  // segment can re-suggest sensible numbers without ever clobbering
  // a value the user deliberately set.
  const [userOverrides, setUserOverrides] = useState({
    forecast_horizon: false,
    lookback: false,
  });
  // Most recent auto-suggested values. Drives the "Auto-tuned" hint
  // so users can see why we picked these numbers and that they're
  // changeable.
  const [autoSuggested, setAutoSuggested] = useState({
    forecast_horizon: null,
    lookback: null,
  });

  // Get workspace ID from header/localStorage and check for cloned model config
  useEffect(() => {
    const id = localStorage.getItem('WORKSPACE_ID');
    if (id) {
      setWorkspaceId(id);
      setFormValues(prev => ({
        ...prev,
        workspace_id: id
      }));
    }

    // Check if we have a cloned model configuration
    const cloneConfig = localStorage.getItem('CLONE_MODEL_CONFIG');
    if (cloneConfig) {
      try {
        const parsedConfig = JSON.parse(cloneConfig);
        
        // Store the parsed config for later use
        setClonedConfig(parsedConfig);
        setIsCloning(true);
        
        // Apply non-reference data immediately
        const initialFormValues = {
          name: parsedConfig.name || '',
          blueprint: parsedConfig.blueprint || '',
          segment: parsedConfig.segment || '',
          forecast_horizon: parsedConfig.forecast_horizon || 1,
          promotion_mode: parsedConfig.promotion_policy?.mode || 'manual',
          // Trigger mode is derived: an enabled data trigger wins, then a
          // recurring schedule (non-empty days), else run-once.
          training_mode: parsedConfig.retrain_triggers?.on_new_data?.enabled
            ? 'data'
            : ((parsedConfig.training_schedule?.days || []).length ? 'schedule' : 'once'),
          retrain_min_rows: parsedConfig.retrain_triggers?.on_new_data?.min_rows || 100,
          retrain_cooldown: parsedConfig.retrain_triggers?.on_new_data?.cooldown_seconds
            || DEFAULT_RETRAIN_PACING,
          monthly_budget: parsedConfig.budget?.monthly_credit_cap || '',
          challenger_blueprints: parsedConfig.challenger_blueprints || [],
          training_schedule: parsedConfig.training_schedule || {
            start_time: '00:00:00',
            end_time: '23:59:59',
            days: [],
            frequency: '86400'
          },
          workspace_id: id
        };
        
        setFormValues(initialFormValues);

        // Cloned values are deliberate user input — don't let
        // auto-defaults overwrite them when the segment loads.
        setUserOverrides({ forecast_horizon: true, lookback: true });

        // Clear the clone config to prevent it from being used again
        localStorage.removeItem('CLONE_MODEL_CONFIG');
      } catch (err) {
        console.error('Error parsing cloned model config:', err);
      }
    }
  }, []);

  // Helper function to normalize UIDs for comparison
  const normalizeUID = (uid) => {
    if (!uid) return '';
    if (typeof uid === 'string') return uid;
    if (typeof uid === 'object') {
      if (uid.$oid) return uid.$oid;
      return uid.toString();
    }
    return String(uid);
  };

  // Fetch blueprints and segments on component mount
  useEffect(() => {
    if (workspaceId) {
      // In foundation-mode the user doesn't pick a blueprint at all,
      // so we skip the blueprint list fetch (saves an API round trip
      // + avoids the "no blueprints yet" empty-state UI). Segments
      // are still required regardless of mode.
      if (!isFoundationMode) {
        fetchBlueprints({ workspace_id: workspaceId });
        // Foundation models are picker entries too — any of them can
        // join the pipeline's model pool via the unified models array.
        fetchPublicFoundationModels({ kind: 'foundation', limit: 100 });
      }
      fetchSegments({ workspace_id: workspaceId });
    }
  }, [fetchBlueprints, fetchPublicFoundationModels, fetchSegments, workspaceId, isFoundationMode]);

  // Everything pickable in the models multi-select: the workspace's own
  // models first, then the foundation catalog. One list, one uid space —
  // the backend accepts any of these uids in the ``models`` array.
  const pickerModels = useMemo(() => {
    const own = blueprints || [];
    const seen = new Set(own.map((b) => normalizeUID(b.uid)));
    const foundation = (foundationModels || [])
      .filter((m) => m?.kind === 'foundation_backbone' && !seen.has(normalizeUID(m.uid)));
    return [...own, ...foundation];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprints, foundationModels]);

  // Auto-select the analysis-recommended blueprint. The workspace analysis
  // worker picks the model and materialises a single "Recommended" blueprint
  // (flagged ``recommended``); we pre-select it so the user doesn't have to
  // design or pick a model unless they want to.
  useEffect(() => {
    if (isFoundationMode || isCloning || clonedConfig) return;
    if (formValues.blueprint) return;
    if (!blueprints || blueprints.length === 0) return;
    const recommended = blueprints.find(
      (b) => b && (b.recommended || (b.metadata && b.metadata.recommended)),
    );
    if (recommended) {
      setFormValues((prev) => (prev.blueprint ? prev : { ...prev, blueprint: recommended.uid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprints, isFoundationMode, isCloning, clonedConfig]);

  // Pull the user's current balance once so the cost estimator can
  // surface "this covers ~N months" alongside the projected spend.
  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  // Fetch foundation backbone metadata when in foundation-mode. The
  // synthesized public-blueprint detail endpoint already returns
  // backbones in a blueprint-shaped envelope (see
  // ``api/blueprint/foundation_views.py``) so we reuse the existing
  // BLUEPRINT_DETAIL action without server-side changes.
  useEffect(() => {
    if (isFoundationMode && foundationUid) {
      dispatch(getBlueprintDetail(foundationUid));
    }
    return () => {
      if (isFoundationMode) dispatch(clearBlueprintDetail());
    };
  }, [dispatch, foundationUid, isFoundationMode]);

  // Once we know the foundation, prefill a sensible default name +
  // lookback so the user can hit Create with one click. They can still
  // override either field. Lookback default comes from the backbone's
  // ``supports.max_context`` (clamped to a reasonable training window)
  // when present.
  useEffect(() => {
    if (!foundationDetail) return;
    setFormValues((prev) => {
      const next = { ...prev };
      if (!next.name) {
        const base = foundationDetail.name || 'Foundation tuning';
        next.name = `${base} pipeline`;
      }
      if (!next.lookback) {
        const supports = foundationMeta?.supports || {};
        const maxCtx = Number(supports.max_context);
        // Cap default lookback at 1024 to keep training fast for a
        // first run; user can crank it up explicitly.
        if (Number.isFinite(maxCtx) && maxCtx > 0) {
          next.lookback = String(Math.min(1024, maxCtx));
        } else if (foundationMeta?.family === 'predictfm') {
          next.lookback = '1024';
        }
      }
      return next;
    });
  }, [foundationDetail, foundationMeta]);

  // ---------------------------------------------------------------
  // Auto-tuned defaults for forecast_horizon (+ lookback in
  // foundation mode), seeded from the picked segment so the user's
  // first attempt actually works. Triggers when segment, blueprint,
  // or foundation context changes; respects userOverrides so we
  // never clobber a value the user explicitly typed.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (segmentsLoading || (!isFoundationMode && blueprintsLoading)) return;
    if (!formValues.segment) return;

    const selectedSegment = segments?.find(
      (s) => normalizeUID(s.uid) === normalizeUID(formValues.segment)
    );
    if (!selectedSegment) return;

    const selectedBlueprint = pickerModels?.find(
      (b) => normalizeUID(b.uid) === normalizeUID(formValues.blueprint)
    );
    const firstPickIsFoundation = isFoundationMode
      || selectedBlueprint?.kind === 'foundation_backbone';

    // sequence_length is fixed by the blueprint in non-foundation mode.
    const existingSeqLen = selectedBlueprint?.training_details?.sequence_length
      || selectedBlueprint?.foundation?.lookback;

    // Map UI mode -> blueprint kind for the validator. Foundation
    // pipelines don't use train/val/test splits so the math
    // collapses to a single lookback+horizon<=n_rows check.
    const pipelineKind = firstPickIsFoundation
      ? 'foundation'
      : (selectedBlueprint?.kind || 'custom');

    const supports = (
      isFoundationMode
        ? foundationMeta?.supports
        : selectedBlueprint?.foundation?.supports
    ) || {};
    const suggestion = suggestPipelineDefaults(selectedSegment, selectedBlueprint?.data_split, {
      kind: pipelineKind,
      existingSequenceLength: isFoundationMode ? null : existingSeqLen,
      maxHorizon: supports.max_horizon,
      maxContext: supports.max_context,
      // Framework drives a tighter default horizon for tree models
      // (one model per forecast step) so we don't auto-suggest a horizon
      // that would OOM XGBoost/sklearn.
      framework: selectedBlueprint?.framework
        || selectedBlueprint?.metadata?.framework
        || selectedBlueprint?.model_blueprint?.metadata?.framework,
    });
    if (!suggestion) return;

    setFormValues((prev) => {
      const next = { ...prev };
      // Only override fields the user hasn't touched. The "value of
      // 1" check is to catch the initial-state placeholder so a fresh
      // form gets defaulted on first segment pick even though no
      // typing has happened.
      if (!userOverrides.forecast_horizon &&
          (Number(prev.forecast_horizon) <= 1 || Number(prev.forecast_horizon) !== suggestion.forecastHorizon)) {
        next.forecast_horizon = suggestion.forecastHorizon;
      }
      if (isFoundationMode && !userOverrides.lookback &&
          (!prev.lookback || Number(prev.lookback) !== suggestion.sequenceLength)) {
        next.lookback = String(suggestion.sequenceLength);
      }
      return next;
    });

    setAutoSuggested({
      forecast_horizon: userOverrides.forecast_horizon ? null : suggestion.forecastHorizon,
      lookback: (isFoundationMode && !userOverrides.lookback) ? suggestion.sequenceLength : null,
    });
  // We deliberately do NOT depend on formValues.forecast_horizon /
  // formValues.lookback to avoid re-firing whenever the user edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.segment, formValues.blueprint,
    segments, pickerModels, foundationMeta,
    isFoundationMode, segmentsLoading, blueprintsLoading,
  ]);

  // Apply cloned blueprint and segment only after blueprints and segments are loaded
  useEffect(() => {
    if (clonedConfig && !blueprintsLoading && !segmentsLoading && blueprints && segments) {
      
      // Normalize the cloned IDs
      const normalizedBlueprintId = normalizeUID(clonedConfig.blueprint);
      const normalizedSegmentId = normalizeUID(clonedConfig.segment);
      
      // Find the matching blueprint and segment objects
      const matchedBlueprint = blueprints.find(bp => normalizeUID(bp.uid) === normalizedBlueprintId);
      const matchedSegment = segments.find(seg => normalizeUID(seg.uid) === normalizedSegmentId);
      
      
      // Collect any warnings
      const warnings = [];
      if (!matchedBlueprint && normalizedBlueprintId) {
        warnings.push(`The model from the original pipeline (${normalizedBlueprintId}) is not available in this workspace. Please select a different model.`);
      }
      if (!matchedSegment && normalizedSegmentId) {
        warnings.push(`The segment from the original pipeline (${normalizedSegmentId}) is not available in this workspace. Please select a different segment.`);
      }
      if (warnings.length > 0) {
        setCloneWarnings(warnings);
      }
      
      // Use much longer delay to ensure DOM is fully rendered
      setTimeout(() => {
        // Create a fresh clone of the form values to break reference equality
        const newFormValues = JSON.parse(JSON.stringify({
          ...formValues,
          blueprint: matchedBlueprint ? matchedBlueprint.uid : '',
          segment: matchedSegment ? matchedSegment.uid : ''
        }));
        
        
        // Set the values with a new object reference
        setFormValues(newFormValues);
        
        // Force re-render after setting values
        setComponentKey(prevKey => prevKey + 1);
      }, 500);
      
      // Clear the cloned config so this only runs once
      setClonedConfig(null);
    }
  }, [clonedConfig, blueprints, segments, blueprintsLoading, segmentsLoading, formValues]);
  
  // For debugging - log all form value changes
  useEffect(() => {
  }, [formValues]);
  
  // Use a ref to access the select elements directly
  const blueprintSelectRef = useRef(null);
  const segmentSelectRef = useRef(null);
  
  // Helper function to trigger a synthetic change event
  const triggerSelectChange = (elementRef, value) => {
    if (!elementRef.current) return;
    
    try {
      // Find the select input element
      const selectElement = elementRef.current.querySelector('input');
      if (selectElement) {
        // Create a synthetic change event
        const event = new Event('change', { bubbles: true });
        
        // Set the value
        Object.defineProperty(event, 'target', {
          writable: false,
          value: { name: selectElement.name, value: value }
        });
        
        // Dispatch the event
        selectElement.dispatchEvent(event);
      }
    } catch (error) {
      console.error("Error triggering synthetic event:", error);
    }
  };
  
  // Effect to directly trigger select changes after component updates
  useEffect(() => {
    if (isCloning && formValues.blueprint && blueprintSelectRef.current) {
      triggerSelectChange(blueprintSelectRef, formValues.blueprint);
    }
    
    if (isCloning && formValues.segment && segmentSelectRef.current) {
      triggerSelectChange(segmentSelectRef, formValues.segment);
    }
  }, [isCloning, formValues.blueprint, formValues.segment, componentKey]);

  // Update useEffect to monitor create model status
  useEffect(() => {
    if (modelCreateResponse?.data?.uid) {
      setSuccess(true);
      // Navigate immediately using window.location
      window.location.href = `/pipeline/${modelCreateResponse.data.uid}`;
    }
    
    if (modelCreateError) {
      setError(modelCreateError);
      setIsSubmitting(false);
    }
    
    // Cleanup effect
    return () => {
      dispatch(clearState());
    };
  }, [modelCreateResponse, modelCreateError, navigate, dispatch]);

  // Enhanced input handler for select components
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    
    if (name === 'forecast_horizon') {
      setUserOverrides(prev => ({ ...prev, forecast_horizon: true }));
      setAutoSuggested(prev => ({ ...prev, forecast_horizon: null }));
      setFormValues(prev => ({
        ...prev,
        [name]: parseInt(value, 10) || 0
      }));
    } else if (name === 'lookback') {
      setUserOverrides(prev => ({ ...prev, lookback: true }));
      setAutoSuggested(prev => ({ ...prev, lookback: null }));
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === 'blueprint' || name === 'segment') {
      // Special handling for blueprint and segment to ensure we store the raw value
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, []);

  // Whether the resolved foundation backbone wants a user-chosen
  // lookback. Most backbones expose ``supports.max_context``; the
  // predictfm family always tunes lookback. When neither applies
  // we hide the field entirely so we don't ask for input the trainer
  // can't use.
  const showLookbackField = useMemo(() => {
    if (!isFoundationMode) return false;
    if (foundationMeta?.family === 'predictfm') return true;
    const max = Number(foundationMeta?.supports?.max_context);
    return Number.isFinite(max) && max > 0;
  }, [isFoundationMode, foundationMeta]);

  // Update the submit handler to use Redux action
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // The trigger mode maps onto the two backend fields:
    //   * once     -> empty schedule days (never schedule-fires; the
    //                 create endpoint queues ONE immediate run), trigger off
    //   * schedule -> the configured schedule, trigger off
    //   * data     -> empty schedule days (immediate first run so there's
    //                 a model to retrain), trigger ON
    const effectiveSchedule = formValues.training_mode === 'schedule'
      ? formValues.training_schedule
      : { ...formValues.training_schedule, days: [] };

    // Automation settings — same shape for both pipeline modes.
    const automation = {
      promotion_policy: {
        mode: formValues.promotion_mode,
        demote_previous: true,
      },
      retrain_triggers: {
        on_new_data: {
          enabled: formValues.training_mode === 'data',
          min_rows: Number(formValues.retrain_min_rows) || 100,
          cooldown_seconds: Number(formValues.retrain_cooldown)
            || DEFAULT_RETRAIN_PACING,
        },
      },
      budget: {
        monthly_credit_cap: formValues.monthly_budget
          ? Number(formValues.monthly_budget)
          : null,
      },
    };

    // One universal ``models`` array for every kind (custom / BYOM /
    // foundation): the entries form a pool — every run trains all of
    // them and the best score wins. ``forecast_horizon`` stays
    // pipeline-level (shared by all entries); foundation entries
    // carry their own knobs (intensity, lookback).
    let models;
    if (isFoundationMode) {
      const foundationEntry = {
        model: foundationDetail?.uid || foundationUid,
        intensity: formValues.training_intensity || 'adapt',
      };
      if (showLookbackField && formValues.lookback) {
        foundationEntry.lookback = Number(formValues.lookback);
      }
      models = [foundationEntry];
    } else {
      models = [
        { model: formValues.blueprint },
        ...(formValues.challenger_blueprints || []).map((uid) => ({ model: uid })),
      ];
    }

    const submitData = {
      name: formValues.name,
      models,
      segment: formValues.segment,
      workspace_id: formValues.workspace_id || workspaceId,
      forecast_horizon: Number(formValues.forecast_horizon) || 1,
      training_schedule: effectiveSchedule,
      active: true,
      ...automation,
    };
    createModel(submitData);
  }, [
    formValues, createModel, isFoundationMode, foundationDetail,
    foundationUid, showLookbackField,
    workspaceId,
  ]);
  
  // Field label with help icon
  const FieldLabel = ({ label, helpText }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Typography variant="body2" fontWeight={500}>{label}</Typography>
      {helpText && (
        <Tooltip title={helpText} arrow placement="top">
          <InfoIcon
            fontSize="small"
            sx={{ ml: 1, color: 'text.secondary', opacity: 0.7, fontSize: '0.9rem', cursor: 'help' }}
          />
        </Tooltip>
      )}
    </Box>
  );

  // Shared design primitives — kept identical to the goal- and
  // segment-studio surfaces so all "create" pages look the same.
  const accent = (key) => theme.palette[key]?.main || theme.palette.primary.main;

  const CardHead = ({ icon, title, helper }) => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );

  const SelectCard = ({ color, icon, title, tagline, description, active, onClick, disabled }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative',
        p: 1.25,
        borderRadius: 2,
        border: `1.5px solid ${active ? color : theme.palette.divider}`,
        bgcolor: active ? alpha(color, 0.1) : 'transparent',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 120ms ease',
        '&:hover': disabled ? {} : { borderColor: color, bgcolor: alpha(color, 0.06) },
      }}
    >
      {active && <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 16, color }} />}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: tagline || description ? 0.25 : 0, pr: active ? 2.5 : 0 }}>
        {icon && <Box sx={{ display: 'flex', color }}>{icon}</Box>}
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
      </Stack>
      {tagline && <Typography variant="caption" sx={{ color, fontWeight: 600, display: 'block' }}>{tagline}</Typography>}
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
          {description}
        </Typography>
      )}
    </Box>
  );

  const grid3 = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
    gap: 1,
  };

  // Add an effect to debug the selected values for blueprints and segments
  useEffect(() => {
    if (formValues.blueprint) {
      const matchedBlueprint = blueprints?.find(b => 
        normalizeUID(b.uid) === normalizeUID(formValues.blueprint)
      );
    }
    
    if (formValues.segment) {
      const matchedSegment = segments?.find(s => 
        normalizeUID(s.uid) === normalizeUID(formValues.segment)
      );
    }
  }, [formValues.blueprint, formValues.segment, blueprints, segments]);

  // One picker drives the unified ``models`` array — a pool of peers:
  // every selection trains each run and the best score wins. Any mix
  // is safe — custom models and foundation models alike — because all
  // of them train on the pipeline's segment, whose features/labels
  // fix the prediction target, with the shared forecast horizon, so
  // cycle scores are directly comparable.
  const renderBlueprintSelect = () => {
    if (blueprintsLoading) {
      return <CircularProgress size={24} />;
    }

    if (!pickerModels || pickerModels.length === 0) {
      return <Typography color="text.secondary">No models available</Typography>;
    }

    const selectedIds = [
      ...(formValues.blueprint ? [formValues.blueprint] : []),
      ...(formValues.challenger_blueprints || []),
    ];

    const isByomId = (uid) => pickerModels.find(
      (b) => normalizeUID(b.uid) === normalizeUID(uid)
    )?.kind === 'byom';

    const handleBlueprintsChange = (e) => {
      const raw = typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value;
      let next = raw.filter(Boolean);
      // BYOM models arrive pre-trained and can't join a training
      // pool - when one is picked it becomes the sole selection
      // (most recent pick wins).
      const byomPick = next.find(isByomId);
      if (byomPick && next.length > 1) {
        next = [next[next.length - 1]];
      }
      setFormValues((prev) => ({
        ...prev,
        blueprint: next[0] || '',
        challenger_blueprints: next.slice(1),
      }));
    };

    const nameOf = (uid) => pickerModels.find(
      (b) => normalizeUID(b.uid) === normalizeUID(uid)
    )?.name || normalizeUID(uid);

    const byomSelected = selectedIds.some(isByomId);
    const ownModels = pickerModels.filter((b) => b.kind !== 'foundation_backbone');
    const foundationOptions = pickerModels.filter((b) => b.kind === 'foundation_backbone');

    return (
      <Box>
        <FormControl fullWidth size="small">
          <Select
            multiple
            name="blueprint"
            value={selectedIds}
            onChange={handleBlueprintsChange}
            displayEmpty
            renderValue={(selected) => (selected && selected.length
              ? (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((uid) => (
                    <Chip
                      key={normalizeUID(uid)}
                      size="small"
                      variant="outlined"
                      label={nameOf(uid)}
                    />
                  ))}
                </Stack>
              )
              : <Typography component="span" color="text.secondary">Select one or more models…</Typography>)}
          >
            {ownModels.length > 0 && (
              <ListSubheader disableSticky>Your models</ListSubheader>
            )}
            {ownModels.map((blueprint) => (
              <MenuItem
                key={normalizeUID(blueprint.uid)}
                value={blueprint.uid}
              >
                {blueprint.name || normalizeUID(blueprint.uid)}
                {blueprint.kind === 'byom' ? ' · BYOM' : ''}
              </MenuItem>
            ))}
            {foundationOptions.length > 0 && (
              <ListSubheader disableSticky>Foundation models</ListSubheader>
            )}
            {foundationOptions.map((blueprint) => (
              <MenuItem
                key={normalizeUID(blueprint.uid)}
                value={blueprint.uid}
              >
                {blueprint.name || normalizeUID(blueprint.uid)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {byomSelected && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            BYOM models arrive pre-trained: this pipeline won't run training cycles and is ready to promote right away. The model's signature is checked against the segment when you save.
          </Typography>
        )}
        {selectedIds.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            All {selectedIds.length} train on the same data every run; the best score wins. Each adds one run's cost.
          </Typography>
        )}
      </Box>
    );
  };

  const renderSegmentSelect = () => {
    if (segmentsLoading) {
      return <CircularProgress size={24} />;
    }
    
    if (!segments || segments.length === 0) {
      return <Typography color="text.secondary">No segments available</Typography>;
    }
    
    // Get the current value
    const currentValue = formValues.segment || '';
    
    // Try to find a match by UID
    const selectedSegment = segments.find(s => {
      const normalizedCurrentValue = normalizeUID(currentValue);
      const normalizedSegmentId = normalizeUID(s.uid);
      const isMatch = normalizedCurrentValue === normalizedSegmentId;
      return isMatch;
    });
    
    
    // Directly handle segment selection
    const handleSegmentChange = (e) => {
      const selectedValue = e.target.value;
      setFormValues({
        ...formValues,
        segment: selectedValue
      });
    };
    
    return (
      <FormControl fullWidth size="small">
        <Select
          name="segment"
          value={currentValue}
          onChange={handleSegmentChange}
          required
          displayEmpty
          renderValue={(selected) => (selected
            ? (segments.find(s => normalizeUID(s.uid) === normalizeUID(selected))?.name || normalizeUID(selected))
            : <Typography component="span" color="text.secondary">Select a segment…</Typography>)}
        >
          {segments.map(segment => (
            <MenuItem
              key={normalizeUID(segment.uid)}
              value={segment.uid}
            >
              {segment.name || normalizeUID(segment.uid)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1500, mx: 'auto' }} key={componentKey}>
        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <PageBreadcrumbs
            items={[
              { text: 'Pipelines', icon: <PipelinesIcon />, link: '/models' },
              { text: isCloning ? 'Clone pipeline' : 'Create pipeline' },
            ]}
            mb={0}
          />
          <Button
            variant="contained"
            startIcon={(modelCreating || isSubmitting) ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
            disabled={
              modelCreating ||
              isSubmitting ||
              !formValues.name ||
              !formValues.segment ||
              (isFoundationMode ? !foundationDetail : !formValues.blueprint)
            }
            onClick={handleSubmit}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {(modelCreating || isSubmitting) ? 'Creating…' : 'Create pipeline'}
          </Button>
        </Stack>

        {isCloning && (
          <Alert severity="info" sx={{ mt: 1 }}>
            You&apos;re creating a new pipeline from a cloned configuration. Adjust anything you need before creating.
          </Alert>
        )}
        {cloneWarnings.length > 0 && cloneWarnings.map((warning, index) => (
          <Alert key={index} severity="warning" sx={{ mt: 1 }}>{warning}</Alert>
        ))}
        {success && (
          <Alert severity="success" sx={{ mt: 1 }}>Pipeline created successfully! Redirecting…</Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1.5 }}>
          {/* Row 1: source + data */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
            {/* Card: name + model source */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <CardHead icon={<InsightsIcon fontSize="small" />} title="Pipeline name" />
                  <CMDarkTextField
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    placeholder="Name this pipeline"
                    required
                  />
                </Box>

                {isFoundationMode ? (
                  <Box>
                    <CardHead
                      icon={<AutoAwesomeIcon fontSize="small" />}
                      title="Foundation model"
                      helper="The foundation model you're tuning on your data."
                    />
                    {foundationLoading && !foundationDetail && (
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">Loading foundation model…</Typography>
                      </Stack>
                    )}
                    {foundationError && (
                      <Alert severity="error">Failed to load foundation model. {foundationError}</Alert>
                    )}
                    {foundationDetail && (
                      <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {foundationDetail.name || foundationUid}
                            </Typography>
                            {foundationDetail.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {foundationDetail.description}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip size="small" label="Foundation model" color="primary" variant="outlined" />
                              {foundationMeta?.family && (
                                <Chip size="small" label={foundationMeta.family} variant="outlined" />
                              )}
                              {foundationMeta?.params && (
                                <Chip size="small" label={`${foundationMeta.params} params`} variant="outlined" />
                              )}
                            </Stack>
                          </Box>
                          <Button size="small" component={RouterLink} to="/browse-public-blueprints">
                            Change
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <CardHead icon={<ModelsIcon fontSize="small" />} title="Models" helper={helpTexts.blueprint} />
                    {renderBlueprintSelect()}
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Card: data + forecast */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2}>
              
                <Box>
                  <CardHead icon={<SegmentsIcon fontSize="small" />} title="Segment" helper={helpTexts.segment} />
                  {renderSegmentSelect()}
                </Box>
              
                <Box>
                  <FieldLabel label="Forecast horizon" helpText={helpTexts.forecast_horizon} />
                  <CMDarkTextField
                    name="forecast_horizon"
                    type="number"
                    value={formValues.forecast_horizon}
                    onChange={handleInputChange}
                    inputProps={{ min: 1 }}
                    helperText={autoSuggested.forecast_horizon
                      ? `Auto-tuned to ${autoSuggested.forecast_horizon} from your segment`
                      : 'Number of future points to forecast'}
                  />
                </Box>
              </Stack>
            </Paper>
          </Box>{/* end Row 1 */}

              {/* Live data-sufficiency preview. Catches the "tiny segment +
                  long horizon" mismatch BEFORE the user clicks Save — the
                  same math runs server-side at create time as a hard
                  guarantee (see core/src/app/segments/data_sufficiency.py). */}
              {(() => {
                const selectedSegment = segments?.find(
                  (s) => normalizeUID(s.uid) === normalizeUID(formValues.segment)
                );
                const selectedBlueprint = blueprints?.find(
                  (b) => normalizeUID(b.uid) === normalizeUID(formValues.blueprint)
                );
                // sequence_length comes from one of three sources
                // depending on the pipeline mode:
                //   * blueprint-mode (custom NN)     -> blueprint.training_details.sequence_length
                //   * blueprint-mode (foundation)    -> blueprint.foundation.lookback
                //   * foundation-mode (this page)    -> formValues.lookback
                const seqLen = selectedBlueprint?.training_details?.sequence_length
                  || selectedBlueprint?.foundation?.lookback
                  || (isFoundationMode ? Number(formValues.lookback) : null);
                if (!selectedSegment || !seqLen) return null;
                const previewKind = isFoundationMode
                  ? 'foundation'
                  : (selectedBlueprint?.kind || 'custom');
                return (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 1.5 }}>
                    <CardHead icon={<SegmentsIcon fontSize="small" />} title="Data check" helper="Whether your segment has enough rows for this lookback and horizon." />
                    <PipelinePreview
                      segment={selectedSegment}
                      sequenceLength={seqLen}
                      forecastHorizon={Number(formValues.forecast_horizon) || 1}
                      dataSplit={selectedBlueprint?.data_split}
                      kind={previewKind}
                    />
                  </Paper>
                );
              })()}

              {/* Foundation tuning section. Only rendered when the
                  page is in foundation-mode and we know the backbone.
                  Intensity is always shown (every backbone supports
                  the three rank tiers). Lookback is conditional on
                  the backbone advertising context support - we don't
                  want to ask for input the trainer can't use. */}
              {isFoundationMode && foundationDetail && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 1.5 }}>
                  <CardHead
                    icon={<TuneIcon fontSize="small" />}
                    title="Foundation tuning"
                    helper="How hard to fine-tune the model on your data."
                  />
                  <Box sx={grid3}>
                    {[
                      { value: 'adapt', title: 'Adapt', tagline: 'rank 4', description: 'Small LoRA — fast and cheap.', accentKey: 'success' },
                      { value: 'specialize', title: 'Specialize', tagline: 'rank 16', description: 'Larger LoRA + adapter heads.', accentKey: 'info' },
                      { value: 'forge', title: 'Forge', tagline: 'rank 32', description: 'Full fine-tune — highest quality.', accentKey: 'warning' },
                    ].map((opt) => (
                      <SelectCard
                        key={opt.value}
                        color={accent(opt.accentKey)}
                        title={opt.title}
                        tagline={opt.tagline}
                        description={opt.description}
                        active={formValues.training_intensity === opt.value}
                        onClick={() => setFormValues((prev) => ({ ...prev, training_intensity: opt.value }))}
                      />
                    ))}
                  </Box>
                  {showLookbackField && (
                    <Box sx={{ mt: 2, maxWidth: 280 }}>
                      <FieldLabel
                        label="Lookback"
                        helpText="Number of past points the model considers when training."
                      />
                      <CMDarkTextField
                        name="lookback"
                        type="number"
                        value={formValues.lookback}
                        onChange={handleInputChange}
                        inputProps={{
                          min: 1,
                          max: Number(foundationMeta?.supports?.max_context) || undefined,
                        }}
                        helperText={
                          foundationMeta?.supports?.max_context
                            ? `Max ${foundationMeta.supports.max_context} for this model`
                            : 'Past context window for training'
                        }
                      />
                    </Box>
                  )}
                </Paper>
              )}

          {/* Row: Training (schedule + data trigger) | Automation
              (promotion, budget) — side by side. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
              mt: 1.5,
              alignItems: 'stretch',
            }}
          >
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <CardHead
              icon={<ScheduleIcon fontSize="small" />}
              title="Training"
              helper="What triggers a training run."
            />
            <Box sx={grid3}>
              {[
                { value: 'once', title: 'Run once', tagline: 'train now', description: 'Trains right away when created. Retrain manually anytime.', accentKey: 'success' },
                { value: 'schedule', title: 'On a schedule', tagline: 'recurring', description: 'Retrains on a fixed cadence — hourly, daily, weekly, or custom.', accentKey: 'info' },
                { value: 'data', title: 'On new data', tagline: 'event-driven', description: 'Retrains when enough new rows land in the segment.', accentKey: 'warning' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  color={accent(opt.accentKey)}
                  title={opt.title}
                  tagline={opt.tagline}
                  description={opt.description}
                  active={formValues.training_mode === opt.value}
                  onClick={() => setFormValues((prev) => ({
                    ...prev,
                    training_mode: opt.value,
                    // Entering schedule mode with an empty (run-once shaped)
                    // schedule seeds a sane daily default for the editor.
                    training_schedule: (opt.value === 'schedule'
                      && !(prev.training_schedule?.days || []).length)
                      ? { start_time: '00:00:00', end_time: '23:59:59',
                          days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                          frequency: '86400' }
                      : prev.training_schedule,
                  }))}
                />
              ))}
            </Box>

            {formValues.training_mode === 'schedule' && (
              <Box sx={{ mt: 1.5 }}>
                <TrainingScheduleField
                  value={formValues.training_schedule}
                  onChange={(next) => setFormValues((prev) => ({ ...prev, training_schedule: next }))}
                  helpText={helpTexts.training_schedule}
                  hideOnce
                  title="Cadence"
                />
              </Box>
            )}

            {formValues.training_mode === 'data' && (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <BoltIcon fontSize="small" color="primary" />
                  <CMDarkTextField
                    name="retrain_min_rows"
                    label="Min new rows"
                    type="number"
                    value={formValues.retrain_min_rows}
                    onChange={handleInputChange}
                    inputProps={{ min: 1 }}
                    sx={{ width: 140 }}
                  />
                  <FormControl size="small" sx={{ width: 170 }}>
                    <Select
                      name="retrain_cooldown"
                      value={formValues.retrain_cooldown}
                      onChange={handleInputChange}
                      renderValue={(selected) => `At most ${
                        (RETRAIN_PACING_OPTIONS.find((o) => o.value === Number(selected))?.label
                          || 'every 30 min').toLowerCase()}`}
                    >
                      {RETRAIN_PACING_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Trains once right away, then whenever this many new rows have landed in the segment — at most once per pacing interval. Budget guardrails still apply.
                </Typography>
              </Box>
            )}

            {formValues.training_mode === 'once' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Trains once right away. You can retrain anytime with "Run training" on the pipeline page.
              </Typography>
            )}
          </Paper>

          {/* Automation: promotion policy + budget guardrail */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <CardHead
              icon={<AutoAwesomeIcon fontSize="small" />}
              title="Automation"
              helper="What happens after each training — promotion and spend guardrails."
            />

            <FieldLabel label="Promotion policy" helpText={helpTexts.promotion_policy} />
            <Box sx={grid3}>
              {[
                { value: 'auto', title: 'Automatic', tagline: 'hands-off', description: 'When a training beats the serving version, promote it and retire the old one.', accentKey: 'success' },
                { value: 'manual', title: 'Ask me first', tagline: 'review each', description: 'You get a prompt to promote the better version or keep what\u2019s live.', accentKey: 'info' },
                { value: 'off', title: 'Off', tagline: 'manual only', description: 'Trainings never suggest promotions. Promote from the trainings list yourself.', accentKey: 'warning' },
              ].map((opt) => (
                <SelectCard
                  key={opt.value}
                  color={accent(opt.accentKey)}
                  title={opt.title}
                  tagline={opt.tagline}
                  description={opt.description}
                  active={formValues.promotion_mode === opt.value}
                  onClick={() => setFormValues((prev) => ({ ...prev, promotion_mode: opt.value }))}
                />
              ))}
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box>
              <FieldLabel label="Monthly budget cap" helpText={helpTexts.budget} />
              <CMDarkTextField
                name="monthly_budget"
                type="number"
                value={formValues.monthly_budget}
                onChange={handleInputChange}
                placeholder="No cap"
                inputProps={{ min: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Credits / month. When reached, automated runs pause until next month — manual runs still work.
              </Typography>
            </Box>
          </Paper>
          </Box>{/* end Training | Automation row */}

          {/* Architecture graph preview — neural-network blueprints only.
              Full width, compact height. Fed with the REAL shapes the
              trainer will use: the chosen segment's feature count
              (input width), its label count and the forecast horizon
              (the auto-appended output head), so the picture matches
              the model that actually trains. */}
          {(() => {
            if (isFoundationMode) return null;
            const selectedBlueprint = blueprints?.find(
              (b) => normalizeUID(b.uid) === normalizeUID(formValues.blueprint)
            );
            const nnLayers = selectedBlueprint?.model_blueprint?.layers;
            const fw = String(
              selectedBlueprint?.framework
              || selectedBlueprint?.metadata?.framework
              || selectedBlueprint?.model_blueprint?.metadata?.framework
              || ''
            ).toLowerCase();
            const isNeuralNet = Array.isArray(nnLayers)
              && nnLayers.length > 0
              && !selectedBlueprint?.model_blueprint?.estimator
              && (fw === '' || fw.includes('tensor') || fw.includes('keras')
                  || fw.includes('torch') || fw.includes('pytorch'));
            if (!isNeuralNet) return null;

            const selectedSegment = segments?.find(
              (s) => normalizeUID(s.uid) === normalizeUID(formValues.segment)
            );
            const baseFeatures = selectedSegment?.features?.length || 0;
            const engineered = (selectedSegment?.engineered_features || [])
              .filter((ef) => ef && ef.name).length;
            const inputSize = baseFeatures + engineered || null;
            const outputLabels = selectedSegment?.labels?.length || null;

            return (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 1.5 }}>
                <CardHead
                  icon={<AccountTreeIcon fontSize="small" />}
                  title="Architecture"
                  helper={selectedSegment ? 'Shaped to your segment.' : 'Pick a segment to see real input/output shapes.'}
                />
                <NeuralNetGraph
                  layers={nnLayers}
                  inputSize={inputSize}
                  outputLabels={outputLabels}
                  forecastHorizon={Number(formValues.forecast_horizon) || 1}
                  height={280}
                />
              </Paper>
            );
          })()}

          {/* Cost estimator (compact card with an Explain modal). */}
          <Box sx={{ mt: 1.5 }}>
            {(() => {
              const firstPick = pickerModels?.find(
                (b) => normalizeUID(b.uid) === normalizeUID(formValues.blueprint),
              );
              const firstPickIsFoundation = isFoundationMode
                || firstPick?.kind === 'foundation_backbone';
              return (
                <TrainingCostEstimator
                  blueprint={firstPick}
                  segment={segments?.find(
                    (s) => normalizeUID(s.uid) === normalizeUID(formValues.segment),
                  )}
                  isFoundationMode={firstPickIsFoundation}
                  foundationFamily={isFoundationMode
                    ? foundationMeta?.family
                    : firstPick?.foundation?.family}
                  trainingIntensity={formValues.training_intensity}
                  lookback={formValues.lookback}
                  forecastHorizon={formValues.forecast_horizon}
                  extraModelCount={isFoundationMode ? 0 : (formValues.challenger_blueprints?.length || 0)}
                  trainingSchedule={formValues.training_mode === 'schedule'
                    ? formValues.training_schedule
                    : { ...formValues.training_schedule, days: [] }}
                  balance={balance}
                />
              );
            })()}
          </Box>

          {/* Bottom actions */}
          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 1.5 }}>
            <Button variant="outlined" onClick={() => navigate('/pipelines')}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={(modelCreating || isSubmitting) ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
              disabled={
                modelCreating ||
                isSubmitting ||
                !formValues.name ||
                !formValues.segment ||
                (isFoundationMode ? !foundationDetail : !formValues.blueprint)
              }
            >
              {(modelCreating || isSubmitting) ? 'Creating…' : 'Create pipeline'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

const mapStateToProps = (state) => ({
  blueprints: state.blueprints_list?.response?.models || [],
  // Foundation catalog rows for the unified picker — any foundation
  // model can join the pipeline's model pool.
  foundationModels: state.blueprints_public_list?.response?.models || [],
  segments: state.segments_list?.response?.segments || [],
  blueprintsLoading: state.blueprints_list?.isLoading || false,
  segmentsLoading: state.segments_list?.isLoading || false,
  modelCreating: state.model_create?.isLoading || false,
  modelCreateResponse: state.model_create?.response || null,
  modelCreateError: state.model_create?.error || null,
  // Foundation-mode reuses the BLUEPRINT_DETAIL action (the public
  // blueprint endpoint already synthesizes backbone rows in a
  // blueprint-shaped envelope), so we surface its slice of the store
  // here too.
  foundationDetail: state.blueprint_detail?.response || null,
  foundationLoading: state.blueprint_detail?.isLoading || false,
  foundationError: state.blueprint_detail?.error || null,
  // Live credit balance for the cost-estimator's "your balance covers
  // ~N months" footer. Optional - the estimator gracefully omits the
  // footer when the balance hasn't loaded yet.
  balance: state.billing_balance?.response || null,
});

const mapDispatchToProps = (dispatch) => ({
  fetchBlueprints: (params) => dispatch(fetchBlueprints(params)),
  fetchPublicFoundationModels: (filters) => dispatch(fetchPublicBlueprints(filters)),
  fetchSegments: (params) => dispatch(fetchSegments(params)),
  createModel: (data) => dispatch(createModelRequest(data)),
  loadBalance: () => dispatch(fetchBalance()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateModelPage); 